import React, { useEffect, useState } from 'react';
import { ActionIcon, Center, Flex, Loader, Select, Text, Tooltip } from '@mantine/core';
import { IconCategory, IconHeadphones, IconUser } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { RouteUtils } from '@bobex/react-core/routes/RouteUtils';
import { MessageUtil } from '@bobex/react-core/utils/MessageUtil';
import { BusinessTypesModelAPI } from '@bobex/react-core/domain/BusinessTypeModel/BusinessTypesModelAPI';
import '@bobex/react-core/components/GenericSearch/searchForm.css';

/**
 * Builds incall URL template for the given context and typeId.
 * Use __AFF__ in the URL and replace with selected affiliate when opening.
 */
function getIncallUrlTemplate(context: string, typeId: string): string {
  if (context === 'nl') {
    return `https://www.renovatiegids.nl/bobex-form/typeid/NL${typeId}?aff=__AFF__&utm_source=incall-lead-2026`;
  }
  return `https://www.renovatie-gids.be/bobex-form/typeid/${typeId}?aff=__AFF__&utm_source=incall-lead-2026`;
}

function getAffOptions(context: string): { value: string; label: string }[] {
  if (context === 'nl') {
    return [{ value: 'jimbo_nl', label: 'Jim Geldhof' }];
  }
  return [
    { value: 'wanagogo_nl', label: 'Intercessor' },
    { value: 'jimbo_nl', label: 'Jim Geldhof' },
  ];
}

export default function IncallTabContent() {
  const { t } = useTranslation();
  const context = RouteUtils.getContext() ?? 'be';
  const affOptions = getAffOptions(context);
  const showAffSelect = affOptions.length > 1;
  const defaultAff = affOptions[0]?.value ?? 'jimbo_nl';

  const [incallOptions, setIncallOptions] = useState<
    { value: string; label: string; url: string }[]
  >([]);
  const [incallLoading, setIncallLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [selectedIncall, setSelectedIncall] = useState<string | null>(null);
  const [aff, setAff] = useState<string>(defaultAff);

  useEffect(() => {
    let cancelled = false;
    setIncallLoading(true);
    setLoadError(null);
    BusinessTypesModelAPI.getPartnerBusinessTypes()
      .then((list) => {
        if (cancelled) return;
        const arr = Array.isArray(list) ? list : [];
        const options: { value: string; label: string; url: string }[] = [];
        for (const item of arr) {
          try {
            const labelObj = item?.label ?? null;
            const displayName = labelObj ? MessageUtil.getMessage(labelObj) : '';
            const val = item?.value != null ? String(item.value) : '';
            if (val) {
              const name = displayName || val;
              options.push({
                value: val,
                label: `${val} - ${name}`,
                url: getIncallUrlTemplate(context, val),
              });
            }
          } catch (_) {
            // skip invalid item
          }
        }
        setIncallOptions(options);
      })
      .catch((err) => {
        console.error('Failed to load incall categories', err);
        if (!cancelled) setLoadError(err?.message || 'Failed to load categories');
      })
      .finally(() => {
        if (!cancelled) setIncallLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [context]);

  const selectedOption = selectedIncall
    ? incallOptions.find((o) => o.value === selectedIncall)
    : null;
  const goUrl = selectedOption ? selectedOption.url.replace('__AFF__', aff) : '';
  const canGo = Boolean(goUrl);

  if (loadError) {
    return (
      <Flex direction="column" gap="md" p="md">
        <Text size="sm" c="red">
          {loadError}
        </Text>
      </Flex>
    );
  }

  return (
    <Flex direction="column" gap="md" p="md" style={{ width: '100%' }}>
      {showAffSelect && (
        <Select
          label={t('Affiliate')}
          size="sm"
          leftSectionPointerEvents="none"
          leftSection={<IconUser size={18} />}
          data={affOptions.map((o) => ({ value: o.value, label: o.label }))}
          value={aff}
          onChange={(v) => v && setAff(v)}
          placeholder={t('Affiliate')}
          style={{ width: '100%' }}
        />
      )}
      <Select
        label={t('Incall category')}
        placeholder={t('Incall categ')}
        size="sm"
        leftSectionPointerEvents="none"
        leftSection={<IconCategory size={18} />}
        data={incallOptions.map((o) => ({ value: o.value, label: o.label }))}
        value={selectedIncall}
        onChange={setSelectedIncall}
        disabled={incallLoading}
        searchable
        style={{ width: '100%' }}
      />
      {incallLoading && (
        <Flex align="center" gap="xs">
          <Loader size="sm" />
          <Text size="sm">{t('Loading...')}</Text>
        </Flex>
      )}
      <Tooltip label={!canGo ? t('Select a category') : undefined}>
        <span>
          <ActionIcon
            component="a"
            href={canGo ? goUrl : undefined}
            target="_blank"
            rel="noopener noreferrer"
            variant="filled"
            color="blue"
            size="lg"
            className="link"
            w="100%"
            disabled={!canGo}
            aria-label={t('Go')}
          >
            <Center>
              <IconHeadphones size={20} />
            </Center>
          </ActionIcon>
        </span>
      </Tooltip>
    </Flex>
  );
}
